/* Boonsoir 2.0 — section micro-interactions (shared helper for section files).
   [data-fx] gets .fx-in once it enters the viewport; the section CSS decides what that means.
   Everything is visible without JS, with reduced motion and in the Theme Editor. */
(() => {
  if (window.BoonsoirFX) return;
  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const design = !!(window.Shopify && window.Shopify.designMode);
  const EASE = 'cubic-bezier(.2,0,0,1)';
  const still = () => reduce.matches || design;

  /* ---------- In-view trigger ---------- */
  let io = null;
  const show = (el) => {
    if (el.classList.contains('fx-in')) return;
    el.classList.add('fx-in');
    el.dispatchEvent(new CustomEvent('fx:in', { bubbles: false }));
  };
  function observe(scope = document) {
    const items = [...scope.querySelectorAll('[data-fx]:not(.fx-in)')];
    if (scope.matches && scope.matches('[data-fx]:not(.fx-in)')) items.push(scope);
    if (!items.length) return;
    if (still() || !('IntersectionObserver' in window)) { items.forEach(show); return; }
    if (!io) {
      io = new IntersectionObserver((entries) => {
        entries.forEach((en) => { if (en.isIntersecting) { show(en.target); io.unobserve(en.target); } });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.15 });
    }
    items.forEach((el) => io.observe(el));
  }
  const showAll = () => document.querySelectorAll('[data-fx]').forEach(show);

  /* ---------- Stroke drawing: normalise every shape to pathLength 1 ---------- */
  function prepDraw(scope = document) {
    scope.querySelectorAll('[data-fx-draw]:not([data-fx-draw-ready])').forEach((wrap) => {
      wrap.querySelectorAll('path, circle, ellipse, rect, line, polyline, polygon').forEach((s, i) => {
        if (s.getAttribute('fill') && s.getAttribute('fill') !== 'none') return; // dots stay dots
        s.setAttribute('pathLength', '1');
        s.style.setProperty('--fx-s', i);
      });
      wrap.setAttribute('data-fx-draw-ready', '');
    });
  }

  /* ---------- Vertical digit roll (countdown, quantities) ----------
     roll(el, text, dir): changes el's text; each changed character slides out and the new one in.
     dir 1 = new comes from below (default), -1 = from above. */
  function cell(ch) {
    const c = document.createElement('span');
    c.className = 'fx-roll';
    const i = document.createElement('span');
    i.className = 'fx-roll__in';
    i.textContent = ch;
    c.appendChild(i);
    return c;
  }
  function roll(el, text, dir = 1) {
    text = String(text);
    const prev = el.dataset.fxRoll;
    if (prev === text) return;
    el.dataset.fxRoll = text;
    if (prev === undefined || still() || prev.length !== text.length || document.hidden) {
      el.textContent = '';
      [...text].forEach((ch) => el.appendChild(cell(ch)));
      return;
    }
    const cells = [...el.children];
    [...text].forEach((ch, n) => {
      if (prev[n] === ch || !cells[n]) return;
      const c = cells[n];
      const old = c.querySelector('.fx-roll__in:last-child');
      const next = document.createElement('span');
      next.className = 'fx-roll__in';
      next.textContent = ch;
      c.appendChild(next);
      const o = { duration: 420, easing: EASE, fill: 'both' };
      if (old) {
        old.classList.add('is-out');
        old.animate([{ transform: 'translateY(0)', opacity: 1 }, { transform: `translateY(${-100 * dir}%)`, opacity: 0 }], o)
          .finished.then(() => old.remove()).catch(() => old.remove());
      }
      next.animate([{ transform: `translateY(${100 * dir}%)`, opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }], o);
    });
  }

  /* ---------- Mechanical counter: each digit column rolls from 0 to its real value ---------- */
  function prepCounter(scope = document) {
    scope.querySelectorAll('[data-fx-counter]:not([data-fx-counter-ready])').forEach((el) => {
      const value = el.textContent.trim();
      if (!value) return;
      el.setAttribute('data-fx-counter-ready', '');
      const sr = document.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = value;
      const face = document.createElement('span');
      face.className = 'fx-counter';
      face.setAttribute('aria-hidden', 'true');
      let col = 0;
      [...value].forEach((ch) => {
        if (/\d/.test(ch)) {
          const c = document.createElement('span');
          c.className = 'fx-counter__col';
          const strip = document.createElement('span');
          strip.className = 'fx-counter__strip';
          strip.style.setProperty('--d', ch);
          strip.style.setProperty('--c', col++);
          for (let n = 0; n <= Number(ch); n++) {
            const d = document.createElement('span');
            d.textContent = String(n);
            strip.appendChild(d);
          }
          c.appendChild(strip);
          face.appendChild(c);
        } else {
          const s = document.createElement('span');
          s.className = 'fx-counter__sep';
          s.textContent = ch;
          face.appendChild(s);
        }
      });
      el.textContent = '';
      el.append(sr, face);
    });
  }

  function init(scope = document) {
    prepDraw(scope);
    prepCounter(scope);
    observe(scope);
  }

  window.BoonsoirFX = { observe, roll, init, still, EASE };
  init();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  window.addEventListener('hashchange', showAll);
  window.addEventListener('beforeprint', showAll);
})();
